/**
 * Staff-only, on an enterprise install: record that the cluster's admins
 * applied a completed SFT set run's sealed adapter as a LoRA module of the
 * vLLM Deployment serving its base (TRA-1600). Staff pick the team model
 * registered for the adapter on the Models page and name the Deployment;
 * the api reads the served model's name from the model's route and writes
 * the deployment into the run's run.json
 * (POST /api/training-runs/{run_id}/deployment). The api decides what may
 * be recorded; a refusal shows in the dialog.
 */

import { useState } from 'react'

import { getApiErrorDetail } from '@/api/client'
import { ConfirmDialog } from '@/components/shared/ConfirmDialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useModels } from '@/hooks/useModels'
import { useRecordTrainingDeployment } from '@/hooks/useTrainingRuns'

/** A Deployment as kubectl names it, namespace/name (the api's DEPLOYMENT_NAME_PATTERN). */
export const DEPLOYMENT_NAME = /^[a-z0-9]([-a-z0-9]*[a-z0-9])?\/[a-z0-9]([-a-z0-9]*[a-z0-9])?$/

export function RecordDeploymentButton({ runId }: { runId: string }): React.JSX.Element {
  const record = useRecordTrainingDeployment()
  const models = useModels()
  const [open, setOpen] = useState(false)
  const [modelId, setModelId] = useState('')
  const [deployment, setDeployment] = useState('')
  // Custom endpoints alone: a catalog model serves nothing trained here.
  const candidates = (models.data ?? []).filter((model) => model.proxy_model_id === null)
  return (
    <>
      <Button
        variant="outline"
        onClick={() => {
          record.reset()
          setOpen(true)
        }}
      >
        Record deployment
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Record the deployment"
        description="The cluster's admins applied the adapter as a LoRA module of the vLLM Deployment serving its base. Pick the team model registered for the adapter on the Models page and name the Deployment. The run then shows where it is served."
        confirmLabel="Record"
        pendingLabel="Recording..."
        isPending={record.isPending}
        confirmDisabled={modelId === '' || !DEPLOYMENT_NAME.test(deployment)}
        error={
          record.isError
            ? (getApiErrorDetail(record.error) ?? 'The deployment was not recorded. Try again')
            : null
        }
        onConfirm={() => {
          record.mutate(
            { runId, data: { model_id: modelId, deployment } },
            {
              onSuccess: () => {
                setOpen(false)
              },
            },
          )
        }}
      >
        <div className="space-y-3">
          <div className="space-y-1">
            <Label htmlFor="deployment-model">Team model</Label>
            <Select value={modelId} onValueChange={setModelId}>
              <SelectTrigger id="deployment-model" aria-label="Team model">
                <SelectValue placeholder="Choose the model registered for the adapter" />
              </SelectTrigger>
              <SelectContent>
                {candidates.map((model) => (
                  <SelectItem key={model.id} value={model.id}>
                    {model.display_name} ({model.slug})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor="deployment-name">Deployment</Label>
            <Input
              id="deployment-name"
              value={deployment}
              onChange={(event) => {
                setDeployment(event.target.value.trim())
              }}
              placeholder="trainloop-workloads/glm-5-3"
            />
            <p className="text-xs text-muted-foreground">The Kubernetes Deployment, as namespace/name.</p>
          </div>
        </div>
      </ConfirmDialog>
    </>
  )
}
